import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import {
  map,
  tap,
  switchMap,
  catchError,
  withLatestFrom
} from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { of } from 'rxjs';
import * as AuthActions from './auth.actions';
import { getAuthState } from './auth.selectors';
import { Store } from '@ngrx/store';
import { AppState } from '../app.states';
import { AuthService } from '../../services/auth/auth.service';
import { notificationMessages } from '@care-portals/data';
import * as AppActions from '../app/app.actions';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    // private apiService: APIService,
    private router: Router,
    private notification: NzNotificationService,
    private store: Store<AppState>
  ) {}

  validateAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateAuth),
      map(() => this.authService.getToken()),
      switchMap(token => {
        if (!token) {
          const currentUrl =
            window.location.pathname
              .replace('login', '')
              .replace('signup', '') + window.location.search;
          return of(
            AuthActions.unauthenticatedState({
              redirectUrl: currentUrl
            })
          );
        } else {
          return of(AuthActions.loadUser({ token }));
        }
      }),
      catchError(error => of(AuthActions.logInFailure(error)))
    )
  );

  unauthenticatedState$ = createEffect(
    () => this.actions$.pipe(ofType(AuthActions.unauthenticatedState)),
    { dispatch: false }
  );

  logIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logIn),
      switchMap(params =>
        this.authService
          .login({
            username: params.username,
            password: params.password
          })
          .pipe(
            map(res => AuthActions.logInSuccess(res)),
            catchError(error => of(AuthActions.logInFailure(error)))
          )
      ),
      catchError(error => of(AuthActions.logInFailure(error)))
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logInSuccess),
      withLatestFrom(this.store.select(getAuthState)),
      tap(([{ token }]) => this.authService.saveToken(token)),
      switchMap(([payload, { redirectUrl }]) => [
        AuthActions.loadUser(payload),
        AuthActions.redirect({ redirectUrl })
      ])
    )
  );

  redirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.redirect),
        tap(({ redirectUrl }) => {
          this.router.navigate([redirectUrl]);
        })
      ),
    { dispatch: false }
  );

  logInFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logInFailure),
        tap(({ error }) => {
          this.notification.error(
            notificationMessages.LoginFailed.title,
            notificationMessages.LoginFailed.message
          );
        })
      ),
    { dispatch: false }
  );

  // called on success of auth
  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUser),
      switchMap(() =>
        this.authService.getUser().pipe(
          map(payload => AuthActions.loadUserSuccess({ payload })),
          catchError(error => of(AuthActions.logInFailure({ error })))
        )
      ),
      catchError(error => of(AuthActions.logInFailure({ error })))
    )
  );

  loadUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserSuccess),
      map(({ payload }) => AppActions.loadAppLayouts({ user: payload }))
    )
  );

  loadUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadUserFailure),
        map(params => params.error),
        map(error =>
          this.notification.error(
            notificationMessages.InvalidSessionSessionTimeout.title,
            notificationMessages.InvalidSessionSessionTimeout.message
          )
        )
      ),
    { dispatch: false }
  );

  logOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logOut),
      tap(() => this.authService.logout()),
      map(res => AuthActions.logOutSuccess())
    )
  );

  logOutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logOutSuccess),
        tap(() => this.router.navigateByUrl('/auth/login'))
      ),
    { dispatch: false }
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      // Implement when API available; for now just emit failure to keep type safety
      switchMap(() => of(AuthActions.logInFailure({ error: 'Not implemented' as any })))
    )
  );
}
